import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { CareerRole_PathwayLevel as PathwayLevel } from '__generated__/graphql-types';

import CareerRoleQuery from 'bundles/unified-career-academy/queries/CareerRoleQuery.graphql';
import type {
  CareerRoleOnRdpFragment,
  CareerRoleQueryQueryVariables,
  CareerRoleQueryQuery as CareerRoleQueryType,
  PathwaySpecializationOnRdpFragment,
  PathwaysOnRdpFragment,
} from 'bundles/unified-career-academy/queries/__generated__/CareerRoleQuery';
import { getPathwayLevel } from 'bundles/unified-common/hooks/useCareerRolesCollection';

export type PathwaySpecializationOnRdp = PathwaySpecializationOnRdpFragment['latestSpecialization'] & {
  proficiencyLevel?: PathwayLevel;
};

export type CareerRoleOnRdpType = Omit<CareerRoleOnRdpFragment, 'pathways'> & {
  pathways: Omit<PathwaysOnRdpFragment, 'entities'> & {
    entities?: Array<PathwaySpecializationOnRdp>;
  };
};

export const normalizeCareerRoleData = (content?: CareerRoleOnRdpFragment | null): CareerRoleOnRdpType | undefined => {
  if (!content) {
    return undefined;
  }

  return {
    ...content,
    pathways: {
      ...content?.pathways,
      id: content?.pathways?.id as string,
      entities: content?.pathways?.entities?.elements
        .map((pathway) => {
          if (
            pathway?.__typename !== 'ProductCard_ProductCard' ||
            pathway?.productTypeAttributes?.__typename !== 'ProductCard_Specialization'
          ) {
            return null;
          }

          const product = pathway?.productTypeAttributes?.canonical?.latestSpecialization;

          return {
            ...product,
            proficiencyLevel: getPathwayLevel(product?.difficultyLevel),
          };
        })
        .filter(Boolean),
    },
  };
};

function useCareerRoleData({ slug, ssr, skip }: { slug: string; ssr: boolean; skip?: boolean }) {
  const { data, loading } = useQuery<CareerRoleQueryType, CareerRoleQueryQueryVariables>(CareerRoleQuery, {
    variables: {
      slug,
      targetDegreesPaginationRequest: {
        page: 0,
        size: 1,
      },
    },
    notifyOnNetworkStatusChange: true,
    ssr,
    skip: skip || !slug,
    errorPolicy: 'all' as const,
    context: { clientName: 'gatewayGql' },
    onError: (error) =>
      Sentry.captureException(error, {
        extra: {
          message: `Error fetching Pathway career role data for the role with the slug "${slug}"`,
        },
      }),
  });

  return {
    data: normalizeCareerRoleData(data?.PathwaySkillCareerRoles?.queryRoleBySlug),
    loading,
  };
}

export default useCareerRoleData;
